// Recetario del catering: MP → recetas base (con merma y rendimiento) → platos (ensamble).
// Catering → Operación → Recetario. Incluye el maestro de precios vigentes de MP.
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ChefHat, Layers, Plus, Search, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Campo, fieldCls, inv, num, toNum } from '@/features/inventario/shared';
import {
  RecetaDetalle, RecetaResumen, cat, catRpc, invalidateCatering, money,
} from './shared';

type Tab = 'recetario' | 'precios';

interface ProductoOpt {
  id: string; codigo: string; nombre: string; unidad_medida: string | null;
  costo_referencial: number; presentacion: string | null; controla_stock: boolean;
}

export function RecetasCatering() {
  const qc = useQueryClient();
  const [tab, setTab] = useState<Tab>('recetario');
  const [filtro, setFiltro] = useState('');
  const [tipoFiltro, setTipoFiltro] = useState('');
  const [selId, setSelId] = useState('');
  const invalidar = () => invalidateCatering(qc);

  // ---- Recetario ----
  const { data: recetas = [], isLoading } = useQuery<RecetaResumen[]>({
    queryKey: ['cat-recetas'],
    queryFn: () => catRpc<RecetaResumen[]>('recetas_listar', { _tipo: null }),
  });
  const { data: detalle } = useQuery<RecetaDetalle>({
    queryKey: ['cat-receta', selId], enabled: !!selId,
    queryFn: () => catRpc<RecetaDetalle>('receta_obtener', { _receta_id: selId }),
  });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({
    queryKey: ['cat-productos-comp'],
    queryFn: () => inv.list<ProductoOpt>('productos',
      '?select=id,codigo,nombre,unidad_medida,costo_referencial,presentacion,controla_stock&deleted_at=is.null&codigo=like.CAT*&order=nombre&limit=600'),
  });

  const filtradas = useMemo(() => recetas.filter((r) =>
    (!tipoFiltro || r.tipo === tipoFiltro) &&
    (!filtro || r.nombre.toLowerCase().includes(filtro.toLowerCase()))), [recetas, filtro, tipoFiltro]);

  // ---- Crear receta ----
  const [nuevoNombre, setNuevoNombre] = useState('');
  const [nuevoTipo, setNuevoTipo] = useState<'subproducto' | 'plato'>('subproducto');
  const [nuevoPorc, setNuevoPorc] = useState('1');
  const crear = useMutation({
    mutationFn: () => catRpc<{ receta_id: string }>('receta_crear', {
      _nombre: nuevoNombre, _tipo: nuevoTipo, _porciones: toNum(nuevoPorc) || 1,
    }),
    onSuccess: (r) => { toast.success('Receta creada'); setNuevoNombre(''); setSelId(r.receta_id); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // ---- Líneas ----
  const [lnComp, setLnComp] = useState('');
  const [lnCant, setLnCant] = useState('');
  const [lnUnidad, setLnUnidad] = useState('kg');
  const [lnMerma, setLnMerma] = useState('');
  const agregarLinea = useMutation({
    mutationFn: () => cat.create('receta_lineas', {
      receta_id: selId, componente_id: lnComp, cantidad: toNum(lnCant),
      unidad: lnUnidad, merma_cant: toNum(lnMerma) || 0,
    }),
    onSuccess: () => { toast.success('Ingrediente agregado'); setLnComp(''); setLnCant(''); setLnMerma(''); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const quitarLinea = useMutation({
    mutationFn: (id: string) => cat.remove('receta_lineas', id),
    onSuccess: () => { toast.success('Ingrediente quitado'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // ---- Maestro de precios MP ----
  const [precioEdit, setPrecioEdit] = useState<Record<string, string>>({});
  const [buscaMp, setBuscaMp] = useState('');
  const guardarPrecio = useMutation({
    mutationFn: ({ id, costo }: { id: string; costo: number }) =>
      inv.update('productos', id, { costo_referencial: costo }),
    onSuccess: () => { toast.success('Precio actualizado — costos recalculados en cascada'); invalidar(); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const mps = useMemo(() => productos.filter((p) =>
    p.controla_stock && (!buscaMp || p.nombre.toLowerCase().includes(buscaMp.toLowerCase()))), [productos, buscaMp]);
  const sinPrecio = useMemo(() => productos.filter((p) => p.controla_stock && !Number(p.costo_referencial)).length, [productos]);

  const compName = (id: string) => productos.find((p) => p.id === id);

  return (
    <div className="space-y-4 p-4">
      <div className="flex items-center gap-2 border-b">
        {([['recetario', 'Recetario'], ['precios', 'Precios de materias primas']] as [Tab, string][]).map(([k, l]) => (
          <button key={k} onClick={() => setTab(k)}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === k ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}>
            {l}{k === 'precios' && sinPrecio > 0 && (
              <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-xs text-amber-800 dark:bg-amber-900/40 dark:text-amber-300">{sinPrecio} sin precio</span>
            )}
          </button>
        ))}
      </div>

      {tab === 'recetario' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-5">
          {/* Listado */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center gap-2 text-base"><ChefHat className="h-4 w-4" /> Recetas y platos</CardTitle>
              <div className="flex gap-2 pt-2">
                <div className="relative flex-1">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <input className={`${fieldCls} pl-8`} placeholder="Buscar…" value={filtro} onChange={(e) => setFiltro(e.target.value)} />
                </div>
                <select className={`${fieldCls} w-36`} value={tipoFiltro} onChange={(e) => setTipoFiltro(e.target.value)}>
                  <option value="">Todos</option>
                  <option value="subproducto">Recetas base</option>
                  <option value="plato">Platos</option>
                </select>
              </div>
            </CardHeader>
            <CardContent className="max-h-[60vh] space-y-1 overflow-y-auto">
              {isLoading && <p className="text-sm text-muted-foreground">Cargando…</p>}
              {filtradas.map((r) => (
                <button key={r.id} onClick={() => setSelId(r.id)}
                  className={`flex w-full items-center justify-between rounded-md border px-3 py-2 text-left text-sm hover:bg-accent ${selId === r.id ? 'border-primary bg-accent' : 'border-transparent'}`}>
                  <span className="flex items-center gap-2">
                    {r.tipo === 'plato'
                      ? <Layers className="h-3.5 w-3.5 shrink-0 text-primary" />
                      : <ChefHat className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
                    <span>
                      {r.nombre}
                      <span className="block text-xs text-muted-foreground">
                        {r.tipo === 'plato' ? 'Plato (ensamble)' : `Batch ${num(r.porciones_std, 0)} porc.`} · {r.num_lineas} ítems
                      </span>
                    </span>
                  </span>
                  <span className="text-right">
                    <span className="font-mono text-sm">{money(r.costo?.costo_porcion)}</span>
                    {r.costo?.sin_costo?.length > 0 && (
                      <span className="flex items-center justify-end gap-1 text-xs text-amber-600"><AlertTriangle className="h-3 w-3" />{r.costo.sin_costo.length} sin precio</span>
                    )}
                  </span>
                </button>
              ))}
              {/* Nueva receta */}
              <div className="mt-3 space-y-2 rounded-md border p-3">
                <p className="text-xs font-medium text-muted-foreground">Nueva receta</p>
                <input className={fieldCls} placeholder="Nombre" value={nuevoNombre} onChange={(e) => setNuevoNombre(e.target.value)} />
                <div className="flex gap-2">
                  <select className={fieldCls} value={nuevoTipo} onChange={(e) => setNuevoTipo(e.target.value as 'subproducto' | 'plato')}>
                    <option value="subproducto">Receta base</option>
                    <option value="plato">Plato (ensamble)</option>
                  </select>
                  <input className={`${fieldCls} w-28`} placeholder="Porciones" value={nuevoPorc} onChange={(e) => setNuevoPorc(e.target.value)} />
                  <Button size="sm" disabled={!nuevoNombre || crear.isPending} onClick={() => crear.mutate()}><Plus className="h-4 w-4" /></Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Detalle */}
          <Card className="lg:col-span-3">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                {detalle ? (
                  <span className="flex flex-wrap items-center gap-2">
                    {detalle.nombre}
                    <span className="rounded-full bg-muted px-2 py-0.5 text-xs">{detalle.tipo === 'plato' ? 'Plato servido' : 'Receta base'}</span>
                    {detalle.costo?.alergenos?.map((a) => (
                      <span key={a} className="rounded-full bg-red-100 px-2 py-0.5 text-xs text-red-700 dark:bg-red-900/40 dark:text-red-300">{a}</span>
                    ))}
                  </span>
                ) : 'Seleccione una receta'}
              </CardTitle>
              {detalle && (
                <p className="text-sm text-muted-foreground">
                  Batch: {num(detalle.porciones_std, 0)} porciones
                  {detalle.rendimiento_cantidad > 0 && ` · Rinde ${num(detalle.rendimiento_cantidad)} ${detalle.rendimiento_unidad}`}
                  {detalle.elaborado_por && ` · Elaborado por ${detalle.elaborado_por}`}
                  {' · '}Costo batch <b>{money(detalle.costo?.costo_batch, 2)}</b>
                  {' → '}<b className="text-primary">{money(detalle.costo?.costo_porcion)} por porción</b>
                </p>
              )}
            </CardHeader>
            {detalle && (
              <CardContent className="space-y-3">
                {detalle.costo?.sin_costo?.length > 0 && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-2 text-sm text-amber-800 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-300">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                    <span>Costo incompleto — sin precio vigente: {detalle.costo.sin_costo.map((s) => s.nombre).join(', ')}.
                      Regístrelo en la pestaña «Precios de materias primas».</span>
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-xs text-muted-foreground">
                        <th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Componente</th>
                        <th className="py-1 pr-2 text-right">Cantidad</th><th className="py-1 pr-2">Unidad</th>
                        <th className="py-1 pr-2 text-right">Merma</th><th className="py-1 pr-2 text-right">Costo</th><th />
                      </tr>
                    </thead>
                    <tbody>
                      {detalle.lineas.map((l, i) => (
                        <tr key={l.id} className="border-b last:border-0">
                          <td className="py-1.5 pr-2 text-muted-foreground">{i + 1}</td>
                          <td className="py-1.5 pr-2">
                            {l.nombre}
                            {l.subreceta_id && (
                              <button className="ml-2 rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary hover:bg-primary/20"
                                onClick={() => setSelId(l.subreceta_id!)}>subreceta ↗</button>
                            )}
                          </td>
                          <td className="py-1.5 pr-2 text-right font-mono">{num(l.cantidad)}</td>
                          <td className="py-1.5 pr-2">{l.unidad}</td>
                          <td className="py-1.5 pr-2 text-right font-mono">{l.merma_cant > 0 ? num(l.merma_cant) : '—'}</td>
                          <td className="py-1.5 pr-2 text-right font-mono">{money(l.costo_linea, 2)}</td>
                          <td className="py-1.5 text-right">
                            <Button variant="ghost" size="sm" onClick={() => quitarLinea.mutate(l.id)}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                          </td>
                        </tr>
                      ))}
                      <tr className="font-medium">
                        <td colSpan={5} className="py-1.5 pr-2 text-right">Total batch</td>
                        <td className="py-1.5 pr-2 text-right font-mono">{money(detalle.costo?.costo_batch, 2)}</td>
                        <td />
                      </tr>
                    </tbody>
                  </table>
                </div>

                <div className="grid grid-cols-1 gap-2 rounded-md border p-3 md:grid-cols-5">
                  <Campo label="Componente (MP o receta)" className="md:col-span-2">
                    <select className={fieldCls} value={lnComp} onChange={(e) => {
                      setLnComp(e.target.value);
                      const p = compName(e.target.value);
                      if (p) setLnUnidad(p.codigo.startsWith('CATRB') || p.codigo.startsWith('CATPL') ? 'porcion' : (p.unidad_medida ?? 'kg'));
                    }}>
                      <option value="">Seleccionar…</option>
                      {productos.map((p) => <option key={p.id} value={p.id}>{p.nombre}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Cantidad"><input className={fieldCls} value={lnCant} onChange={(e) => setLnCant(e.target.value)} /></Campo>
                  <Campo label="Unidad">
                    <select className={fieldCls} value={lnUnidad} onChange={(e) => setLnUnidad(e.target.value)}>
                      {['kg', 'l', 'unidad', 'porcion', 'caja'].map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                  </Campo>
                  <Campo label="Merma">
                    <div className="flex gap-2">
                      <input className={fieldCls} value={lnMerma} onChange={(e) => setLnMerma(e.target.value)} placeholder="0" />
                      <Button size="sm" disabled={!lnComp || !lnCant || agregarLinea.isPending} onClick={() => agregarLinea.mutate()}><Plus className="h-4 w-4" /></Button>
                    </div>
                  </Campo>
                </div>
              </CardContent>
            )}
          </Card>
        </div>
      )}

      {tab === 'precios' && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Maestro de precios vigentes</CardTitle>
            <p className="text-sm text-muted-foreground">
              Un cambio aquí recalcula EN CASCADA el costo de todas las recetas, platos y menús que usan la materia prima.
            </p>
            <div className="relative w-72 pt-2">
              <Search className="absolute left-2 top-4.5 h-4 w-4 text-muted-foreground" />
              <input className={`${fieldCls} pl-8`} placeholder="Buscar materia prima…" value={buscaMp} onChange={(e) => setBuscaMp(e.target.value)} />
            </div>
          </CardHeader>
          <CardContent className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-2">N°</th><th className="py-1 pr-2">Materia prima</th>
                  <th className="py-1 pr-2">Unidad</th><th className="py-1 pr-2">Presentación de compra</th>
                  <th className="py-1 pr-2 text-right">Precio vigente</th><th className="py-1 pr-2 text-right">Nuevo precio</th><th />
                </tr>
              </thead>
              <tbody>
                {mps.map((p, i) => (
                  <tr key={p.id} className={`border-b last:border-0 ${!Number(p.costo_referencial) ? 'bg-amber-50 dark:bg-amber-900/10' : ''}`}>
                    <td className="py-1.5 pr-2 text-muted-foreground">{i + 1}</td>
                    <td className="py-1.5 pr-2">
                      {p.nombre}
                      {!Number(p.costo_referencial) && <span className="ml-2 inline-flex items-center gap-1 text-xs text-amber-600"><AlertTriangle className="h-3 w-3" /> sin precio</span>}
                    </td>
                    <td className="py-1.5 pr-2">{p.unidad_medida}</td>
                    <td className="py-1.5 pr-2 text-muted-foreground">{p.presentacion ?? '—'}</td>
                    <td className="py-1.5 pr-2 text-right font-mono">{money(p.costo_referencial)}</td>
                    <td className="py-1.5 pr-2 text-right">
                      <input className={`${fieldCls} w-24 text-right`} placeholder={String(p.costo_referencial ?? '')}
                        value={precioEdit[p.id] ?? ''} onChange={(e) => setPrecioEdit((s) => ({ ...s, [p.id]: e.target.value }))} />
                    </td>
                    <td className="py-1.5 text-right">
                      <Button size="sm" variant="outline" disabled={!precioEdit[p.id] || guardarPrecio.isPending}
                        onClick={() => { guardarPrecio.mutate({ id: p.id, costo: toNum(precioEdit[p.id]) }); setPrecioEdit((s) => ({ ...s, [p.id]: '' })); }}>
                        Guardar
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
